import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { takeNextPath, stashNextPath } from "@/lib/nextPath";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { formatApiErrorDetail, api } from "@/lib/api";
import { GoogleLogo, ArrowRight, Sun, Moon } from "@phosphor-icons/react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";

export default function Login() {
  const { login, register } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [hero, setHero] = useState("https://images.unsplash.com/photo-1525286335722-c30c6b5df541?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600");
  const [headline, setHeadline] = useState("Considered. Monochrome. PGD.");
  const [tagline, setTagline] = useState("Portfolio aesthetic");
  useEffect(() => {
    api.get("/public/settings").then(r => {
      if (r.data?.login_hero_image) setHero(r.data.login_hero_image);
      if (r.data?.login_headline) setHeadline(r.data.login_headline);
      if (r.data?.login_tagline) setTagline(r.data.login_tagline);
    }).catch(() => {});
  }, []);
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = mode === "login"
        ? await login(email, password)
        : await register(email, password, name);
      toast.success(`Welcome, ${u.name}`);
      nav(takeNextPath(u.role), { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    stashNextPath();
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2" data-testid="login-page"
         style={{ background: "var(--bg)", color: "var(--text)" }}>
      <div className="flex flex-col justify-between p-8 md:p-12 border-r-0 md:border-r"
           style={{ borderColor: "var(--hair)" }}>
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="login-brand-link">
            <div className="w-10 h-10 flex items-center justify-center font-semibold"
                 style={{ background: "var(--invert-bg)", color: "var(--invert-text)" }}>P</div>
            <div>
              <p className="font-medium leading-none">PrettyGrlsDesign</p>
              <p className="label leading-none mt-1">Client Portal</p>
            </div>
          </Link>
          <button onClick={toggle} className="btn-ghost p-2" data-testid="login-theme-toggle">
            {theme === "dark" ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
          </button>
        </div>

        <div className="max-w-md w-full my-12">
          <p className="label mb-4">{mode === "login" ? "Welcome back" : "Create your account"}</p>
          <h1 className="title-xl text-5xl md:text-6xl">
            {mode === "login" ? "Sign in." : "Get started."}
          </h1>
          <p className="mt-4 leading-relaxed" style={{ color: "var(--text-dim)" }}>
            {mode === "login"
              ? "Clients and admins log in here. You’ll be routed automatically."
              : "Clients: register to start your onboarding."}
          </p>

          <form onSubmit={submit} className="mt-10 space-y-3" data-testid="login-form">
            {mode === "register" && (
              <input
                data-testid="login-name-input"
                required placeholder="Full name" value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-4"
              />
            )}
            <input
              data-testid="login-email-input"
              required type="email" placeholder="Email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-4"
            />
            <PasswordInput
              testid="login-password-input"
              required placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-4 pr-11"
            />
            {err && <p data-testid="login-error" className="label px-3 py-2" style={{ background: "var(--invert-bg)", color: "var(--invert-text)" }}>{err}</p>}
            <button
              data-testid="login-submit-btn"
              disabled={loading} type="submit"
              className="w-full btn-primary justify-center h-14 disabled:opacity-60"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight size={14} weight="bold" />
            </button>
          </form>

          <div className="mt-4 flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="label">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button
            data-testid="google-signin-btn"
            onClick={google}
            className="w-full mt-4 btn-ghost justify-center h-14"
          >
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </button>

          <button
            data-testid="toggle-auth-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
            className="label mt-6 underline underline-offset-4"
          >
            {mode === "login" ? "New client? Create account" : "Already have an account? Sign in"}
          </button>
        </div>

        <p className="label">© PrettyGrlsDesign</p>
      </div>

      <div className="hidden md:block relative" style={{ background: "var(--invert-bg)" }}>
        <img alt="Editorial" className="absolute inset-0 w-full h-full object-cover" src={hero} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top right, rgba(0,0,0,0.85), rgba(0,0,0,0.4), transparent)" }} />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <p className="label" style={{ color: "rgba(255,255,255,0.7)" }}>{tagline}</p>
          <p className="title-xl text-5xl mt-3">
            {headline}
          </p>
        </div>
      </div>
    </div>
  );
}
